export const MOBILE_ANNOTATION_EDITOR_KEYBOARD_THRESHOLD_PX = 96;
export const ANNOTATION_SUBMIT_DISABLED_TOOLTIP_UPDATE_IN_PROGRESS = 'Temporarily disabled — update in progress.';
export const ANNOTATION_SUBMIT_DISABLED_TOOLTIP_AUDIO_NOTE_SAVING = 'Save or finish saving the audio note before submitting.';
export const ANNOTATION_SUBMIT_DISABLED_TOOLTIP_MEDIA_UPLOADING = 'Finish uploading attached media before submitting.';

/**
 * Filters visual viewport deltas so only a real software keyboard resize moves
 * the annotation editor. The calculation intentionally keys off viewport
 * height only so touch scrolling and browser-chrome rebasing do not nudge the
 * editor while the user is moving the reader content.
 */
export const resolveMobileAnnotationEditorKeyboardOffset = ({
    layoutViewportHeight,
    visualViewportHeight,
    editorHasFocus,
    minimumKeyboardOffset = MOBILE_ANNOTATION_EDITOR_KEYBOARD_THRESHOLD_PX,
}: {
    layoutViewportHeight: number,
    visualViewportHeight: number,
    editorHasFocus: boolean,
    minimumKeyboardOffset?: number,
}): number => {
    if (!editorHasFocus) {
        return 0;
    }

    const occludedHeight = Math.max(
        layoutViewportHeight - visualViewportHeight,
        0,
    );

    return occludedHeight >= minimumKeyboardOffset
        ? occludedHeight
        : 0;
};

/**
 * The docked toolkit shell can stay logically active for a short closing delay
 * after its content has already been hidden. Treat presses during that hidden
 * closing window as a reopen so the button never needs a second press just to
 * clear stale closing state.
 */
export const resolveStationaryToolbarToggleAction = ({
    stationaryToolbarIsActive,
    stationaryToolbarIsClosing,
    showStationaryToolbar,
}: {
    stationaryToolbarIsActive: boolean,
    stationaryToolbarIsClosing: boolean,
    showStationaryToolbar: boolean,
}): 'open' | 'close' => {
    if (stationaryToolbarIsClosing) {
        return 'open';
    }

    if (stationaryToolbarIsActive || showStationaryToolbar) {
        return 'close';
    }

    return 'open';
};

/**
 * Mirrors the stationary toolbar's staggered Transition timing so the editor
 * shell can wait for the real toolbar mount and unmount lifecycle instead of a
 * guessed duration.
 */
export const resolveStationaryToolbarLifecycleDelay = ({
    activeToolGroupCount,
    staggerOffsetDuration,
    toolTransitionDuration,
    maxTransitionDuration,
    includesExpandAllGroup = true,
}: {
    activeToolGroupCount: number,
    staggerOffsetDuration: number,
    toolTransitionDuration: number,
    maxTransitionDuration: number,
    includesExpandAllGroup?: boolean,
}): number => {
    const renderedToolGroupCount = activeToolGroupCount + (includesExpandAllGroup ? 1 : 0);

    return Math.min(
        maxTransitionDuration,
        (renderedToolGroupCount * staggerOffsetDuration) + toolTransitionDuration,
    );
};

/**
 * Keeps the stationary toolbar shell and its inner content in sync so a close
 * never leaves hidden toolbar content marked as visible, and an open only
 * schedules content reveal when the shell is genuinely entering.
 */
export const resolveStationaryToolbarVisibilityAction = ({
    dockAnnotationEditorToViewport,
    showStationaryToolbar,
    stationaryToolbarIsActive,
    stationaryToolbarIsClosing,
}: {
    dockAnnotationEditorToViewport: boolean,
    showStationaryToolbar: boolean,
    stationaryToolbarIsActive: boolean,
    stationaryToolbarIsClosing: boolean,
}): 'hide' | 'show' | 'delay-show' | 'none' => {
    if (!stationaryToolbarIsActive) {
        return showStationaryToolbar ? 'hide' : 'none';
    }

    if (stationaryToolbarIsClosing || showStationaryToolbar) {
        return 'none';
    }

    if (dockAnnotationEditorToViewport) {
        return 'show';
    }

    return 'delay-show';
};

/**
 * Keeps the annotation submit control disabled whenever editor-level work is
 * still in flight. Audio-note draft/save work takes precedence so the tooltip
 * explains the blocker the user actually needs to resolve first.
 */
export const resolveAnnotationSubmitButtonDisabledReason = ({
    interactionDisabled,
    numberSavingAudioNotes,
    numberDirtyAudioNotes,
    uploadingMediaCount,
}: {
    interactionDisabled: boolean,
    numberSavingAudioNotes: number,
    numberDirtyAudioNotes: number,
    uploadingMediaCount: number,
}): string => {
    if (interactionDisabled) {
        return ANNOTATION_SUBMIT_DISABLED_TOOLTIP_UPDATE_IN_PROGRESS;
    }

    if (numberSavingAudioNotes > 0 || numberDirtyAudioNotes > 0) {
        return ANNOTATION_SUBMIT_DISABLED_TOOLTIP_AUDIO_NOTE_SAVING;
    }

    if (uploadingMediaCount > 0) {
        return ANNOTATION_SUBMIT_DISABLED_TOOLTIP_MEDIA_UPLOADING;
    }

    return '';
};
